// src/components/molecules/ActionButtons/ActionButtons.jsx
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Menu, MenuItem, Divider, ListItemIcon, ListItemText, Typography, CircularProgress } from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  SentimentVerySatisfied as PositiveIcon,
  SentimentSatisfied as NeutralIcon,
  SentimentVeryDissatisfied as NegativeIcon,
  OpenInNew as OpenInNewIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Collections as CollectionsIcon,
  Label as LabelIcon,
  Public as PublicIcon,
  AutoAwesome as AutoAwesomeIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useMIA } from '../../../context/MIAContext';
import { analyzeSentiment } from '../../../services/api';
import { colors } from '../../../design-system/tokens';

import IconButtonWithTooltip from '../IconButtonWithTooltip';
import OpenArticleButton from '../../atoms/OpenArticleButton/OpenArticleButton';

/**
 * ActionButtons - Componente molecular com conjunto de botões de ação para artigos
 * 
 * Este componente segue o princípio do Atomic Design como uma "molécula",
 * combinando vários átomos (IconButtonWithTooltip) para criar um componente
 * funcional mais complexo.
 */
const ActionButtons = ({
  article,
  onAction,
  showRemoveFromCollection = false,
  showRemoveFromTag = false,
  layout = 'vertical',
  setHover,
  onUpdateSentiment,
  glassmorphismOverlay = false,
  onDelete,
  menuOnly = false,
  buttonSx = {},
  iconColor = '#ffffff',
  showMoreOptions = true,
  showOpenOriginal = true,
  favoriteActiveColor = '#FFD600',
}) => {
  // Estados para controlar a abertura dos menus
  const [anchorEl, setAnchorEl] = useState(null);
  const [sentimentMenuAnchorEl, setSentimentMenuAnchorEl] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { isSentimentAIEnabled } = useMIA();

  // Funções para abrir e fechar os menus
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSentimentMenuOpen = (event) => {
    event.stopPropagation();
    setSentimentMenuAnchorEl(event.currentTarget);
  };

  const handleSentimentMenuClose = () => {
    setSentimentMenuAnchorEl(null);
  };

  // Função para lidar com ações
  const handleAction = (actionType, event) => {
    if (event) {
      event.stopPropagation();
    }
    onAction(actionType, article);
    setHover && setHover(false);
  };

  // Função para atualizar o sentimento
  const handleUpdateSentiment = (sentiment, event) => {
    if (event) {
      event.stopPropagation();
    }

    if (onUpdateSentiment) {
      onUpdateSentiment(article._id, sentiment);
    } else {
      onAction('toggleSentiment', article);
    }

    setSentimentMenuAnchorEl(null);
    setHover && setHover(false);
  };

  // Função para analisar sentimento com IA
  const handleAnalyzeSentiment = async (event) => {
    if (event) {
      event.stopPropagation();
    }
    if (isAnalyzing || !isSentimentAIEnabled()) return;

    setIsAnalyzing(true);
    try {
      const response = await analyzeSentiment(article._id);
      const { sentiment } = response.data;

      if (onUpdateSentiment) {
        onUpdateSentiment(article._id, sentiment);
      }
      setSentimentMenuAnchorEl(null);
    } catch (error) {
      console.error('Erro ao analisar sentimento:', error);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Função para abrir o link público
  const handleOpenPublicLink = (event) => {
    if (event) {
      event.stopPropagation();
    }
    const publicUrl = `${window.location.origin}/article/${article._id}`;
    window.open(publicUrl, '_blank');
  };

  // Estilo base para os botões de ação com efeito glassmorphism
  // Reduzido para melhor visualização em telas menores
  const buttonStyle = {
    color: iconColor,
    fontSize: 18,
    background: 'rgba(0,0,0,0.42)',
    borderRadius: '50%',
    p: 0.5,
    m: 0,
    minWidth: 'unset',
    transition: 'background-color 0.12s ease',
    '&:hover': {
      background: 'rgba(0,0,0,0.62)'
    },
    // Garantir que todos os ícones SVG herdem a cor branca
    '& .MuiSvgIcon-root': {
      color: 'inherit',
      fontSize: 18
    },
    ...buttonSx
  };

  // Função utilitária para centralizar as ações
  const getArticleActions = (article, handlers = {}) => [
    {
      label: article.isRead ? 'Marcar como não lido' : 'Marcar como lido',
      icon: article.isRead
        ? <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
        : <VisibilityOffIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: (e) => handlers.onToggleRead && handlers.onToggleRead(article, e),
    },
    {
      label: 'Adicionar Tag',
      icon: <LabelIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: (e) => handlers.onAddTag && handlers.onAddTag(article, e),
    },
    {
      label: 'Adicionar à Coleção',
      icon: <CollectionsIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: (e) => handlers.onAddToCollection && handlers.onAddToCollection(article, e),
    },
    handlers.showRemoveFromCollection && {
      label: 'Remover da Coleção',
      icon: <CollectionsIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: (e) => handlers.onRemoveFromCollection && handlers.onRemoveFromCollection(article, e),
    },
    handlers.showRemoveFromTag && {
      label: 'Remover da Tag',
      icon: <LabelIcon fontSize="small" sx={{ mr: 1 }} />,
      onClick: (e) => handlers.onRemoveFromTag && handlers.onRemoveFromTag(article, e),
    },
    (article.mediaType === 'Online' && article.siteUrl) && {
      label: 'Corrigir Extração (IA Premium)',
      icon: <AutoAwesomeIcon fontSize="small" sx={{ mr: 1, color: '#21CBF3' }} />,
      onClick: (e) => handlers.onSmartRescrape && handlers.onSmartRescrape(article, e),
    },
  ].filter(Boolean);

  if (menuOnly) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <IconButtonWithTooltip
          title="Mais Opções"
          icon={<MoreVertIcon sx={{ fontSize: 18, color: 'inherit' }} />}
          onClick={handleMenuOpen}
          sx={buttonStyle}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          onClick={(e) => e.stopPropagation()}
        >
          {getArticleActions(article, {
            onToggleRead: (a, e) => handleAction('toggleRead', e),
            onAddTag: (a, e) => handleAction('addTag', e),
            onAddToCollection: (a, e) => handleAction('addCollection', e),
            showRemoveFromCollection,
            showRemoveFromTag,
            onRemoveFromCollection: (a, e) => handleAction('removeFromCollection', e),
            onRemoveFromTag: (a, e) => handleAction('removeFromTag', e),
            onSmartRescrape: (a, e) => handleAction('rescrapeSmart', e),
          }).map(action => (
            <MenuItem
              key={action.label}
              onClick={(e) => { e.stopPropagation(); action.onClick && action.onClick(e); handleMenuClose(); }}
              sx={action.color === 'error' ? { color: 'error.main' } : {}}
            >
              {action.icon}
              {action.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: layout === 'list' ? 'row' : 'row',
        gap: 0.35,
        alignItems: 'center',
        justifyContent: layout === 'list' ? 'flex-end' : 'center',
        flexWrap: 'wrap',
        background: 'none',
        boxShadow: 'none',
        backdropFilter: 'none',
        borderRadius: 0,
        p: 0,
        fontSize: 'unset',
        width: '100%',
        maxWidth: '100%',
        height: 'unset',
        ...(glassmorphismOverlay && {
          background: 'rgba(0,0,0,0.44)',
          borderRadius: 12,
          padding: '8px 16px',
        })
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Botão para abrir o link original (ou página da mídia impressa) */}
      {showOpenOriginal && (
        <OpenArticleButton
          article={article}
          url={article.siteUrl || article.url}
          variant="icon"
          sx={buttonStyle}
        />
      )}

      {/* Botão para abrir link público */}
      <IconButtonWithTooltip
        title="Link Público"
        icon={<PublicIcon sx={{ fontSize: 18, color: '#ffffff' }} />}
        onClick={handleOpenPublicLink}
        sx={buttonStyle}
      />

      {/* Botão para favoritar/desfavoritar */}
      <IconButtonWithTooltip
        title={article.isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'}
        icon={article.isFavorite ?
          <StarIcon sx={{ color: favoriteActiveColor, fontSize: 18 }} /> :
          <StarBorderIcon sx={{ fontSize: 18, color: '#ffffff' }} />
        }
        onClick={(e) => handleAction('toggleFavorite', e)}
        sx={buttonStyle}
      />



      {/* Botão para sentimento */}
      <IconButtonWithTooltip
        title="Mudar Sentimento"
        icon={
          article.sentiment === 'positive' ?
            <PositiveIcon sx={{ color: colors.sentiment.positive.main, fontSize: 18 }} /> :
            article.sentiment === 'negative' ?
              <NegativeIcon sx={{ color: colors.sentiment.negative.main, fontSize: 18 }} /> :
              <NeutralIcon sx={{ color: colors.sentiment.neutral.main, fontSize: 18 }} />
        }
        onClick={handleSentimentMenuOpen}
        sx={buttonStyle}
      />

      {/* Menu de sentimentos */}
      <Menu
        anchorEl={sentimentMenuAnchorEl}
        open={Boolean(sentimentMenuAnchorEl)}
        onClose={handleSentimentMenuClose}
        onClick={(e) => e.stopPropagation()}
        PaperProps={{
          sx: { minWidth: 180 }
        }}
      >
        {/* Seção Manual */}
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.disabled', fontWeight: 600, display: 'block' }}>
          MANUAL
        </Typography>
        <MenuItem onClick={(e) => handleUpdateSentiment('positive', e)}>
          <ListItemIcon><PositiveIcon sx={{ color: colors.sentiment.positive.main }} /></ListItemIcon>
          <ListItemText>Positivo</ListItemText>
        </MenuItem>
        <MenuItem onClick={(e) => handleUpdateSentiment('neutral', e)}>
          <ListItemIcon><NeutralIcon sx={{ color: '#9e9e9e' }} /></ListItemIcon>
          <ListItemText>Neutro</ListItemText>
        </MenuItem>
        <MenuItem onClick={(e) => handleUpdateSentiment('negative', e)}>
          <ListItemIcon><NegativeIcon sx={{ color: colors.sentiment.negative.main }} /></ListItemIcon>
          <ListItemText>Negativo</ListItemText>
        </MenuItem>

        <Divider sx={{ my: 1 }} />

        {/* Seção MIA */}
        <Typography variant="caption" sx={{ px: 2, py: 0.5, color: 'text.disabled', fontWeight: 600, display: 'block' }}>
          MIA (IA)
        </Typography>
        <MenuItem
          onClick={handleAnalyzeSentiment}
          disabled={isAnalyzing || !isSentimentAIEnabled()}
        >
          <ListItemIcon>
            {isAnalyzing ? (
              <CircularProgress size={20} color="secondary" />
            ) : (
              <AutoAwesomeIcon sx={{ color: isSentimentAIEnabled() ? 'secondary.main' : 'text.disabled' }} />
            )}
          </ListItemIcon>
          <ListItemText
            primary={isAnalyzing ? 'Analisando...' : 'Analisar com IA'}
            secondary={!isSentimentAIEnabled() ? 'Ative sua MIA' : null}
            primaryTypographyProps={{ color: isSentimentAIEnabled() ? 'text.primary' : 'text.disabled' }}
            secondaryTypographyProps={{ sx: { fontSize: '0.7rem' } }}
          />
        </MenuItem>
      </Menu>

      {showMoreOptions && (
        <>
          {/* Botão de lido/não lido */}
          <IconButtonWithTooltip
            title={article.isRead ? 'Marcar como não lido' : 'Marcar como lido'}
            icon={article.isRead ?
              <VisibilityIcon sx={{ fontSize: 18, color: '#ffffff' }} /> :
              <VisibilityOffIcon sx={{ fontSize: 18, color: '#ffffff' }} />
            }
            onClick={(e) => handleAction('toggleRead', e)}
            sx={buttonStyle}
          />

          {/* Botão de Adicionar Tag */}
          <IconButtonWithTooltip
            title="Adicionar Tag"
            icon={<LabelIcon sx={{ fontSize: 18, color: '#ffffff' }} />}
            onClick={(e) => handleAction('addTag', e)}
            sx={buttonStyle}
          />

          {/* Botão de Coleção */}
          <IconButtonWithTooltip
            title="Adicionar à Coleção"
            icon={<CollectionsIcon sx={{ fontSize: 18, color: '#ffffff' }} />}
            onClick={(e) => handleAction('addCollection', e)}
            sx={buttonStyle}
          />

          {showRemoveFromCollection && (
            <IconButtonWithTooltip
              title="Remover da Coleção"
              icon={<CollectionsIcon sx={{ fontSize: 18, color: 'error.main' }} />}
              onClick={(e) => handleAction('removeFromCollection', e)}
              sx={{
                ...buttonStyle,
                '& .MuiSvgIcon-root': { color: 'error.main', fontSize: 18 }
              }}
            />
          )}

          {showRemoveFromTag && (
            <IconButtonWithTooltip
              title="Remover da Tag"
              icon={<LabelIcon sx={{ fontSize: 18, color: 'error.main' }} />}
              onClick={(e) => handleAction('removeFromTag', e)}
              sx={{
                ...buttonStyle,
                '& .MuiSvgIcon-root': { color: 'error.main', fontSize: 18 }
              }}
            />
          )}
        </>
      )}

      {/* Botão para enviar como newsletter */}
      <IconButtonWithTooltip
        title="Enviar como Newsletter"
        icon={<EmailIcon sx={{ fontSize: 18, color: '#ffffff' }} />}
        onClick={(e) => handleAction('sendNewsletter', e)}
        sx={buttonStyle}
      />

      {/* Botão para editar */}
      <IconButtonWithTooltip
        title="Editar"
        icon={<EditIcon sx={{ fontSize: 18, color: '#ffffff' }} />}
        onClick={(e) => handleAction('edit', e)}
        sx={buttonStyle}
      />

      {/* Botão para Corrigir Extração (IA Premium) */}
      {(article.mediaType === 'Online' && article.siteUrl) && (
        <IconButtonWithTooltip
          title="Corrigir Extração (IA Premium)"
          icon={<AutoAwesomeIcon sx={{ fontSize: 18, color: '#21CBF3' }} />}
          onClick={(e) => handleAction('rescrapeSmart', e)}
          sx={buttonStyle}
        />
      )}

      {/* Botão para excluir */}
      <IconButtonWithTooltip
        title="Excluir"
        icon={<DeleteIcon sx={{ fontSize: 18, color: 'error.main' }} />}
        onClick={(e) => {
          if (onDelete) {
            e.stopPropagation();
            onDelete(e);
            return;
          }

          handleAction('delete', e);
        }}
        sx={{
          ...buttonStyle,
          // Sobrescreve o color:inherit do buttonStyle para que o ícone fique vermelho
          '& .MuiSvgIcon-root': {
            color: 'error.main',
            fontSize: 18
          },
          '&:hover': {
            background: 'rgba(211, 47, 47, 0.55)'
          }
        }}
      />

    </Box>
  );
};

ActionButtons.propTypes = {
  /** Objeto do artigo com propriedades como _id, isFavorite, isRead, sentiment */
  article: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    isFavorite: PropTypes.bool,
    isRead: PropTypes.bool,
    sentiment: PropTypes.oneOf(['positive', 'neutral', 'negative'])
  }).isRequired,

  /** Função para lidar com ações como toggleFavorite, toggleRead, etc. */
  onAction: PropTypes.func.isRequired,

  /** Mostrar opção para remover da coleção */
  showRemoveFromCollection: PropTypes.bool,

  /** Mostrar opção para remover da tag */
  showRemoveFromTag: PropTypes.bool,

  /** Layout dos botões (vertical, horizontal, list) */
  layout: PropTypes.string,

  /** Função para definir o estado de hover */
  setHover: PropTypes.func,

  /** Função para atualizar o sentimento */
  onUpdateSentiment: PropTypes.func,

  /** Propriedade para aplicar efeito glassmorphism no container */
  glassmorphismOverlay: PropTypes.bool,

  /** Função para lidar com a exclusão */
  onDelete: PropTypes.func,

  /** Renderiza apenas o menu de mais opções */
  menuOnly: PropTypes.bool,

  /** Estilos extras aplicados aos botões */
  buttonSx: PropTypes.object,

  /** Cor base dos ícones */
  iconColor: PropTypes.string,

  /** Mostrar botão de mais opções */
  showMoreOptions: PropTypes.bool,
  showOpenOriginal: PropTypes.bool,

  /** Cor da estrela quando o artigo está favoritado */
  favoriteActiveColor: PropTypes.string,
};

export default ActionButtons;

